import React, { useState, useCallback, useEffect } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';

/**
 * Module-level cache for refreshed download URLs.
 * Maps storage path → fresh download URL.
 * This prevents the fail→refresh cycle for every image on the page:
 * once one image refreshes, all others with cached paths load instantly.
 */
const urlCache = new Map<string, string>();

/**
 * Tracks whether any stored URL has failed.
 * Once true, all FirebaseImage instances skip the stored URL and go
 * directly to the cache or getDownloadURL, avoiding the slow error timeout.
 */
let storedUrlsAreStale = false;

interface FirebaseImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** The stored download URL (may be expired) */
  src?: string;
  /** The Firebase Storage path (e.g. "profiles/123_profile.jpg") - used as fallback to refresh expired URLs */
  storagePath?: string;
  /** Callback when a fresh URL is obtained, so the parent can persist it */
  onUrlRefreshed?: (newUrl: string) => void;
  /** Fallback content to show if the image can't be loaded at all */
  fallback?: React.ReactNode;
}

/**
 * Extract the Firebase Storage path from a download URL.
 * URLs look like:
 *   https://firebasestorage.googleapis.com/v0/b/BUCKET/o/ENCODED_PATH?alt=media&token=TOKEN
 */
function extractStoragePath(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const match = urlObj.pathname.match(/\/o\/(.+?)(?:\?|$)/);
    if (match) {
      return decodeURIComponent(match[1]);
    }
    const altMatch = url.match(/\/v0\/b\/[^/]+\/o\/([^?]+)/);
    if (altMatch) {
      return decodeURIComponent(altMatch[1]);
    }
  } catch {
    // URL parsing failed
  }
  return null;
}

/**
 * Resolve the storage path from props — either the explicit storagePath or extracted from the URL.
 */
function resolvePath(src?: string, storagePath?: string): string | null {
  if (storagePath) return storagePath;
  if (src) return extractStoragePath(src);
  return null;
}

/**
 * A drop-in replacement for <img> that handles expired Firebase Storage download URLs.
 *
 * Key optimization: a module-level URL cache ensures that once one image detects
 * stale URLs and refreshes, all subsequent images load instantly from cache
 * without going through the fail→refresh cycle.
 */
export const FirebaseImage: React.FC<FirebaseImageProps> = ({
  src,
  storagePath,
  onUrlRefreshed,
  fallback,
  ...imgProps
}) => {
  const path = resolvePath(src, storagePath);

  // Check cache first — if we already have a fresh URL for this path, use it
  const cachedUrl = path ? urlCache.get(path) : undefined;

  const [currentSrc, setCurrentSrc] = useState<string | undefined>(() => {
    if (cachedUrl) return cachedUrl;
    if (storedUrlsAreStale && path) return undefined; // skip stale stored URL
    return src;
  });
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset state when the src or storagePath prop changes
  useEffect(() => {
    const newCachedUrl = path ? urlCache.get(path) : undefined;
    if (newCachedUrl) {
      setCurrentSrc(newCachedUrl);
    } else if (storedUrlsAreStale && path) {
      setCurrentSrc(undefined);
    } else {
      setCurrentSrc(src);
    }
    setHasTriedRefresh(false);
    setFailed(false);
    setLoading(false);
  }, [src, storagePath, path]);

  // If we know URLs are stale and have no cached URL, proactively refresh
  useEffect(() => {
    if (!currentSrc && path && !hasTriedRefresh && !loading && !failed) {
      refreshUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSrc, path, hasTriedRefresh, loading, failed]);

  const refreshUrl = useCallback(async () => {
    if (!path || hasTriedRefresh || loading) return;

    setLoading(true);
    setHasTriedRefresh(true);

    // Check cache one more time (another instance may have populated it)
    const cached = urlCache.get(path);
    if (cached) {
      setCurrentSrc(cached);
      setLoading(false);
      return;
    }

    try {
      const storageRef = ref(storage, path);
      const freshUrl = await getDownloadURL(storageRef);
      urlCache.set(path, freshUrl);
      setCurrentSrc(freshUrl);
      onUrlRefreshed?.(freshUrl);
    } catch (error) {
      console.error('FirebaseImage: Failed to refresh download URL for path:', path, error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [path, hasTriedRefresh, loading, onUrlRefreshed]);

  const handleError = useCallback(async () => {
    // Mark that stored URLs are stale so other instances skip the stored URL
    storedUrlsAreStale = true;

    if (hasTriedRefresh) {
      setFailed(true);
      return;
    }

    await refreshUrl();
  }, [hasTriedRefresh, refreshUrl]);

  // No image source at all — show fallback immediately
  if (!src && !storagePath) {
    return <>{fallback}</>;
  }

  // All attempts failed
  if (failed) {
    return <>{fallback}</>;
  }

  // Still loading a fresh URL
  if (!currentSrc) {
    return <>{fallback}</>;
  }

  return (
    <img
      {...imgProps}
      src={currentSrc}
      onError={handleError}
    />
  );
};
